/**
 * Signals the header Connect button to show the wallet-attention animation.
 * Used when recharge / refund & delete needs a connected wallet and none is available.
 */
export const WALLET_CONNECT_ATTENTION_EVENT = "idx-wallet-connect-attention";

/** Clears the Connect attention animation (e.g. user dismissed refund confirm). */
export const WALLET_CONNECT_ATTENTION_CLEAR_EVENT =
  "idx-wallet-connect-attention-clear";

/** Ask the header wallet control to open its provider menu — desktop: IDX dropdown (`lg`+); narrower: `@solana/wallet-adapter-react-ui` WalletModal (`WalletConnectButton` listener). */
export const WALLET_OPEN_CONNECT_MENU_EVENT =
  "idx-wallet-open-connect-menu";

/** Broadcast when the wallet is disconnected so every `useWallet` instance clears local state. */
export const WALLET_DISCONNECTED_EVENT = "idx-wallet-disconnected";

/** Broadcast when any instance persists a connection so other `useWallet` hooks hydrate. */
export const WALLET_CONNECTION_UPDATED_EVENT = "idx-wallet-connection-updated";

/**
 * Session flag: user explicitly disconnected. Blocks auto re-attach from extension
 * detect / localStorage hydrate until they connect again (or the tab session ends).
 */
const WALLET_USER_DISCONNECTED_KEY = "idx-wallet-user-disconnected";

export function markWalletUserDisconnected() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(WALLET_USER_DISCONNECTED_KEY, "1");
  } catch {
    /* private mode / blocked storage */
  }
}

export function clearWalletUserDisconnected() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(WALLET_USER_DISCONNECTED_KEY);
  } catch {
    /* noop */
  }
}

export function isWalletUserDisconnected() {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(WALLET_USER_DISCONNECTED_KEY) === "1";
  } catch {
    return false;
  }
}

export function dispatchWalletConnectAttention() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(WALLET_CONNECT_ATTENTION_EVENT));
}

/**
 * Open the header wallet menu: try a real Connect click, then always dispatch
 * `WALLET_OPEN_CONNECT_MENU_EVENT` so the menu opens even when programmatic
 * clicks from embedded widgets (e.g. Jupiter) do not hit React reliably.
 */
export function dispatchWalletOpenConnectMenu() {
  if (typeof window === "undefined") return;
  const reinforceOpen = () => {
    window.dispatchEvent(new CustomEvent(WALLET_OPEN_CONNECT_MENU_EVENT));
  };
  const clickHeaderConnect = () => {
    const btn = document.querySelector(
      "button[data-idx-wallet-connect-cta][type='button']",
    );
    if (btn instanceof HTMLButtonElement && !btn.disabled) {
      btn.click();
      return true;
    }
    return false;
  };
  clickHeaderConnect();
  queueMicrotask(reinforceOpen);
  window.requestAnimationFrame(() => {
    clickHeaderConnect();
    reinforceOpen();
  });
}

export function dispatchWalletConnectionUpdated() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(WALLET_CONNECTION_UPDATED_EVENT));
}

export function clearWalletConnectAttention() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(WALLET_CONNECT_ATTENTION_CLEAR_EVENT));
}

export function dispatchWalletDisconnected() {
  if (typeof window === "undefined") return;
  markWalletUserDisconnected();
  window.dispatchEvent(new CustomEvent(WALLET_DISCONNECTED_EVENT));
}
